import type {
	Component,
	MasterPageDefinition,
	TPAWidgetDataItem,
	TPAWidgetDefinition,
} from '@wix/thunderbolt-becky-types'
import { createCssCompNode } from '../cssCompNode'
import type { FeatureRefs } from '../cssFeatures.types'
import { envRefs } from '@wix/thunderbolt-catharsis'
import type { OtherComponentsResult } from './landingPageCss.types'

const COMPONENTS_TO_DISPLAY_IN_LANDING_PAGE = new Set([
	'PageGroup',
	'PagesContainer',
	'QuickActionBar',
	'BackToTopButton',
	'SiteBackground',
])
const EMPTY_ARRAY: Array<string> = []
const CHAT_WIDGET_ID = '14517f3f-ffc5-eced-f592-980aaa0bbb5c'
const AI_CHAT_WIDGET_ID = 'f6b6e28d-52b2-4de6-aab3-ff2ccad8e229'
const CHAT_WIDGETS = new Set([CHAT_WIDGET_ID, AI_CHAT_WIDGET_ID])

type MasterPageDependecies = {
	data: MasterPageDefinition['data']
	isMobileView: boolean
	children: Array<OtherComponentsResult>
	headerCompLayout: Component['layout']
}

export const landingPagesCss = (isEditor: boolean) =>
	createCssCompNode('landingPages', 'landingPagesCss', {
		getDependencies: (component: Component, refs: FeatureRefs<'landingPages'>) => {
			if (component.type === 'Document') {
				return {
					data: refs.dataQuery<MasterPageDefinition['data']>(component.dataQuery!),
					isMobileView: envRefs.isMobileView,
					children: (component.components || EMPTY_ARRAY).map(refs.landingPagesCss),
				}
			}

			return component.componentType === 'TPAGluedWidget'
				? {
					data: refs.dataQuery<TPAWidgetDefinition['data']>(component.dataQuery!),
				}
				: {
					isAutoHeightWrapper: isEditor ? refs.autoHeightWrapper!(component) : false,
				}
		},
		toViewItem: (component, deps) => {
			if ('isAutoHeightWrapper' in deps) {
				if (COMPONENTS_TO_DISPLAY_IN_LANDING_PAGE.has(component.componentType)) {
					return EMPTY_ARRAY
				}

				return deps.isAutoHeightWrapper ? [`wrapper-${component.id}`] : [component.id]
			}

			const depsData = deps.data as TPAWidgetDataItem & MasterPageDependecies['data']
			if (component.componentType === 'TPAGluedWidget') {
				const isChatWidget = CHAT_WIDGETS.has(depsData.widgetId)
				return deps.data.type === 'TPAWidget' && isChatWidget ? EMPTY_ARRAY : [component.id]
			}

			const { isMobileView, children } = deps as MasterPageDependecies

			const marginTop = depsData.layoutSettings?.headerToPagesGap || '0'
			const marginBottom = depsData.layoutSettings?.pagesToFooterGap || '0'

			return {
				css: isMobileView
					? null
					: {
						'#masterPage:not(.landingPage) #PAGES_CONTAINER': {
							'margin-top': `${marginTop}px`,
							'margin-bottom': `${marginBottom}px`,
						},
					},
				componentsToHide: children
					.flatMap((x) => x)
					.concat('SITE_HEADER-placeholder', 'SITE_FOOTER-placeholder'),
			}
		},
	})
